import React, { useState, useEffect } from 'react';
// Fix: Replaced Playlist icon with ViewList to resolve import conflicts
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Autocomplete,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close,
  Schedule as ScheduleIcon,
  Event,
  Settings,
  Check,
  Error as ErrorIcon,
  PlayArrow,
  Monitor,
  ViewList as PlaylistIcon,
  Person,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';

import { 
  EventStatus,
  Priority
} from '../../types/index';
import type { 
  ScheduleEvent, 
  Device,
  DeviceGroup,
  Playlist,
  Media,
  ScheduleEventCreate,
  ScheduleEventUpdate,
  ScheduleConflict
} from '../../types/index';
import apiClient from '../../services/api';

interface ScheduleEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: ScheduleEvent) => void;
  event?: ScheduleEvent | null;
  scheduleId?: number;
  mode?: 'create' | 'edit';
}

const eventSchema = yup.object({
  name: yup
    .string()
    .required('Название события обязательно')
    .min(3, 'Минимум 3 символа')
    .max(200, 'Максимум 200 символов'),
  description: yup
    .string()
    .max(1000, 'Максимум 1000 символов')
    .nullable(),
  start_time: yup
    .date()
    .required('Время начала обязательно')
    .min(new Date(), 'Время начала не может быть в прошлом'),
  end_time: yup
    .date()
    .required('Время окончания обязательно')
    .min(yup.ref('start_time'), 'Время окончания должно быть позже времени начала'),
  priority: yup
    .number()
    .required('Приоритет обязателен')
    .oneOf([1, 5, 10, 15, 20], 'Некорректный приоритет'),
  status: yup
    .string()
    .required('Статус обязателен')
    .oneOf(['scheduled', 'active', 'completed', 'failed', 'cancelled', 'skipped'], 'Некорректный статус'),
  device_id: yup
    .number()
    .nullable()
    .when('device_group_id', {
      is: (device_group_id: any) => !device_group_id,
      then: (schema) => schema.required('Выберите устройство или группу устройств'),
      otherwise: (schema) => schema.nullable(),
    }),
  device_group_id: yup.number().nullable(),
  playlist_id: yup.number().nullable(),
  media_id: yup
    .number()
    .nullable()
    .when('playlist_id', {
      is: (playlist_id: any) => !playlist_id,
      then: (schema) => schema.required('Выберите плейлист или медиафайл'),
      otherwise: (schema) => schema.nullable(),
    }),
});

const ScheduleEventDialog: React.FC<ScheduleEventDialogProps> = ({
  open,
  onClose,
  onSave,
  event,
  scheduleId,
  mode = 'create',
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [media, setMedia] = useState<Media[]>([]);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);

  const dateLocale = i18n.language === 'ru' ? ru : enUS;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<{
    name: string;
    description?: string;
    start_time: Date;
    end_time: Date;
    priority: Priority;
    status: EventStatus;
    device_id?: number;
    device_group_id?: number;
    playlist_id?: number;
    media_id?: number;
    sync_playback?: boolean;
    sync_start_delay_seconds?: number;
  }>({
    resolver: yupResolver(eventSchema),
    defaultValues: {
      name: '',
      description: '',
      start_time: new Date(),
      end_time: new Date(Date.now() + 60 * 60 * 1000), // +1 час
      priority: 10,
      status: EventStatus.SCHEDULED,
      device_id: undefined,
      device_group_id: undefined,
      playlist_id: undefined,
      media_id: undefined,
      sync_playback: false,
      sync_start_delay_seconds: 10,
    },
    mode: 'onChange',
  });

  const watchedDeviceId = watch('device_id');
  const watchedDeviceGroupId = watch('device_group_id');
  const watchedPlaylistId = watch('playlist_id');
  const watchedMediaId = watch('media_id');
  const watchedSyncPlayback = watch('sync_playback');

  useEffect(() => {
    if (open) {
      loadFormData();
      if (event && mode === 'edit') {
        populateForm();
      } else {
        reset();
      }
    }
  }, [open, event, mode, reset]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const [devicesData, groupsData, playlistsData, mediaData] = await Promise.all([
        apiClient.getDevices(),
        apiClient.getDeviceGroups(),
        apiClient.getPlaylists(),
        apiClient.getMedia(),
      ]);

      setDevices(devicesData.items || []);
      setDeviceGroups(groupsData || []);
      setPlaylists(playlistsData.items || []);
      setMedia(mediaData.items || []);
    } catch (err) {
      console.error('Ошибка загрузки данных формы:', err);
      setError('Ошибка загрузки данных формы');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = () => {
    if (!event) return;

    const formData = {
      name: event.name || '',
      description: event.description || '',
      start_time: new Date(event.start_time),
      end_time: new Date(event.end_time),
      priority: event.priority,
      status: event.status,
      device_id: event.device_id || event.device?.id,
      device_group_id: event.device_group_id || event.device_group?.id,
      playlist_id: event.playlist_id || event.playlist?.id,
      media_id: event.media_id || event.media?.id,
      sync_playback: Boolean(event.playback_settings?.sync_playback),
      sync_start_delay_seconds: Number(event.playback_settings?.sync_start_delay_seconds || 10),
    };

    Object.entries(formData).forEach(([key, value]) => {
      setValue(key as any, value);
    });
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    setError(null);
    setConflicts([]);

    try {
      const eventPayload: ScheduleEventCreate = {
        name: data.name,
        description: data.description || undefined,
        start_time: data.start_time.toISOString(),
        end_time: data.end_time.toISOString(),
        priority: data.priority,
        schedule_id: scheduleId || event?.schedule_id,
        device_id: data.device_id || undefined,
        device_group_id: data.device_group_id || undefined,
        playlist_id: data.playlist_id || undefined,
        media_id: data.media_id || undefined,
        playback_settings: {
          sync_playback: Boolean(data.sync_playback),
          sync_start_delay_seconds: Number(data.sync_start_delay_seconds || 10),
          sync_tolerance_ms: 250,
          drift_correction_threshold_ms: 500,
        },
      };

      if (eventPayload.schedule_id) {
        const foundConflicts = await apiClient.checkScheduleConflicts(eventPayload);
        const relevantConflicts = mode === 'edit' && event
          ? foundConflicts.filter((conflict) => conflict.conflict_event_id !== event.id)
          : foundConflicts;
        if (relevantConflicts.length > 0) {
          setConflicts(relevantConflicts);
          setError('Найдены конфликты расписания. Измените время, устройство или группу.');
          return;
        }
      }

      let result: ScheduleEvent;

      if (mode === 'edit' && event) {
        const updateData: ScheduleEventUpdate = {
          name: eventPayload.name,
          description: eventPayload.description,
          start_time: eventPayload.start_time,
          end_time: eventPayload.end_time,
          priority: eventPayload.priority,
          device_id: eventPayload.device_id,
          device_group_id: eventPayload.device_group_id,
          playlist_id: eventPayload.playlist_id,
          media_id: eventPayload.media_id,
          playback_settings: eventPayload.playback_settings,
        };
        result = await apiClient.updateScheduleEvent(event.id, updateData);
        if (data.status && data.status !== event.status) {
          result = await apiClient.updateScheduleEventStatus(event.id, data.status);
        }
      } else {
        result = await apiClient.createScheduleEvent(eventPayload);
        if (data.status && data.status !== EventStatus.SCHEDULED) {
          result = await apiClient.updateScheduleEventStatus(result.id, data.status);
        }
      }

      onSave(result);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка сохранения события');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setConflicts([]);
    onClose();
  };

  const getPriorityLabel = (priority: Priority): string => {
    switch (priority) {
      case 20: return t('common.priority.critical');
      case 15: return t('common.priority.high');
      case 10: return t('common.priority.normal');
      case 5: return t('common.priority.low');
      case 1: return t('common.priority.veryLow');
      default: return t('common.priority.normal');
    }
  };

  const getStatusLabel = (status: EventStatus): string => {
    switch (status) {
      case 'scheduled': return t('schedule.status.scheduled');
      case 'active': return t('schedule.status.active');
      case 'completed': return t('schedule.status.completed');
      case 'failed': return t('schedule.status.failed');
      case 'cancelled': return t('schedule.status.cancelled');
      case 'skipped': return t('schedule.status.skipped');
      default: return t('schedule.status.scheduled');
    }
  };

  // Очистка взаимоисключающих полей
  const handleDeviceChange = (deviceId: number | null) => {
    setValue('device_id', deviceId || undefined);
    if (deviceId) {
      setValue('device_group_id', undefined);
    }
  };

  const handleDeviceGroupChange = (deviceGroupId: number | null) => {
    setValue('device_group_id', deviceGroupId || undefined);
    if (deviceGroupId) {
      setValue('device_id', undefined);
    }
  };

  const handlePlaylistChange = (playlistId: number | null) => {
    setValue('playlist_id', playlistId || undefined);
    if (playlistId) {
      setValue('media_id', undefined);
    }
  };

  const handleMediaChange = (mediaId: number | null) => {
    setValue('media_id', mediaId || undefined);
    if (mediaId) {
      setValue('playlist_id', undefined);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Event color="primary" />
              <Typography variant="h6">
                {mode === 'edit' 
                  ? t('schedule.events.edit') 
                  : t('schedule.events.create')
                }
              </Typography>
            </Stack>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" noValidate>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {conflicts.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Конфликты расписания</AlertTitle>
                <Stack spacing={0.5}>
                  {conflicts.map((conflict) => (
                    <Typography key={conflict.conflict_event_id} variant="body2">
                      {conflict.conflict_event_name}: {format(new Date(conflict.conflict_start_time), 'dd.MM.yyyy HH:mm')} - {format(new Date(conflict.conflict_end_time), 'HH:mm')}
                      {conflict.conflict_device_name ? `, устройство: ${conflict.conflict_device_name}` : ''}
                      {conflict.conflict_group_name ? `, группа: ${conflict.conflict_group_name}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Основная информация */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.basicInfo')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('schedule.events.title')}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="Введите название события"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('common.description')}
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="Описание события (необязательно)"
                    />
                  )}
                />
              </Grid>

              {/* Время */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.timing')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="start_time"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      label={t('schedule.startTime')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.start_time,
                          helperText: errors.start_time?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="end_time"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      label={t('schedule.endTime')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.end_time,
                          helperText: errors.end_time?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Настройки */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.settings')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.priority}>
                      <InputLabel>{t('schedule.priorityLabel')}</InputLabel>
                      <Select {...field} label={t('schedule.priorityLabel')}>
                        <MenuItem value={1}>{getPriorityLabel(1)}</MenuItem>
                        <MenuItem value={5}>{getPriorityLabel(5)}</MenuItem>
                        <MenuItem value={10}>{getPriorityLabel(10)}</MenuItem>
                        <MenuItem value={15}>{getPriorityLabel(15)}</MenuItem>
                        <MenuItem value={20}>{getPriorityLabel(20)}</MenuItem>
                      </Select>
                      {errors.priority && (
                        <FormHelperText>{errors.priority.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.status}>
                      <InputLabel>{t('schedule.status')}</InputLabel>
                      <Select {...field} label={t('schedule.status')}>
                        <MenuItem value="scheduled">{getStatusLabel(EventStatus.SCHEDULED)}</MenuItem>
                        <MenuItem value="active">{getStatusLabel('active')}</MenuItem>
                        <MenuItem value="completed">{getStatusLabel('completed')}</MenuItem>
                        <MenuItem value="failed">{getStatusLabel(EventStatus.FAILED)}</MenuItem>
                        <MenuItem value="cancelled">{getStatusLabel('cancelled')}</MenuItem>
                        <MenuItem value="skipped">{getStatusLabel(EventStatus.SKIPPED)}</MenuItem>
                      </Select>
                      {errors.status && (
                        <FormHelperText>{errors.status.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Controller
                    name="sync_playback"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={Boolean(field.value)} onChange={(event) => field.onChange(event.target.checked)} />}
                        label="Синхронное воспроизведение"
                      />
                    )}
                  />
                  {watchedSyncPlayback && (
                    <Controller
                      name="sync_start_delay_seconds"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          label="Старт через, сек"
                          size="small"
                          inputProps={{ min: 3, max: 300 }}
                          sx={{ width: 180 }}
                        />
                      )}
                    />
                  )}
                </Stack>
              </Grid>

              {/* Привязка контента */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.content')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Выберите цель показа и контент для воспроизведения
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={devices}
                  getOptionLabel={(option) => option.name}
                  value={devices.find(d => d.id === watchedDeviceId) || null}
                  onChange={(_, value) => handleDeviceChange(value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('schedule.device')}
                      error={!!errors.device_id}
                      helperText={errors.device_id?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Monitor sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.location}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={deviceGroups}
                  getOptionLabel={(option) => option.name}
                  value={deviceGroups.find(group => group.id === watchedDeviceGroupId) || null}
                  onChange={(_, value) => handleDeviceGroupChange(value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Группа устройств"
                      error={!!errors.device_group_id}
                      helperText={errors.device_group_id?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Monitor sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || 'Группа устройств'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={playlists}
                  getOptionLabel={(option) => option.name}
                  value={playlists.find(p => p.id === watchedPlaylistId) || null}
                  onChange={(_, value) => handlePlaylistChange(value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('schedule.playlist')}
                      error={!!errors.playlist_id}
                      helperText={errors.playlist_id?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <PlaylistIcon sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.items_count} элементов
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={media}
                  getOptionLabel={(option) => option.title || option.filename}
                  value={media.find(m => m.id === watchedMediaId) || null}
                  onChange={(_, value) => handleMediaChange(value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('schedule.media')}
                      error={!!errors.media_id}
                      helperText={errors.media_id?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.media_type === 'video' && <PlayArrow sx={{ mr: 2 }} />}
                      {option.media_type === 'image' && <Event sx={{ mr: 2 }} />}
                      {option.media_type === 'audio' && <Event sx={{ mr: 2 }} />}
                      <Box>
                        <Typography variant="body1">
                          {option.title || option.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.media_type} • {Math.round((option.file_size || 0) / 1024 / 1024)} MB
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              {/* Предварительный просмотр */}
              {(watchedDeviceId || watchedDeviceGroupId || watchedPlaylistId || watchedMediaId) && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('schedule.preview')}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {watchedDeviceId && (
                          <Chip
                            icon={<Monitor />}
                            label={`Устройство: ${devices.find(d => d.id === watchedDeviceId)?.name}`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {watchedDeviceGroupId && (
                          <Chip
                            icon={<Monitor />}
                            label={`Группа: ${deviceGroups.find(group => group.id === watchedDeviceGroupId)?.name}`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {watchedPlaylistId && (
                          <Chip
                            icon={<PlaylistIcon />}
                            label={`Плейлист: ${playlists.find(p => p.id === watchedPlaylistId)?.name}`}
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        {watchedMediaId && (
                          <Chip
                            icon={<PlayArrow />}
                            label={`Медиа: ${media.find(m => m.id === watchedMediaId)?.title || media.find(m => m.id === watchedMediaId)?.filename}`}
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={loading || !isValid}
            startIcon={loading ? undefined : <Check />}
          >
            {loading ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ScheduleEventDialog;
